import React, { useEffect, useImperativeHandle, useRef } from "react"
import { devMode } from "../../utils/dev-mode"
import { Button } from "../../components/ui-primitives/button"
import { useMemoizedFn } from "../../hooks/use-memoized-fn"
import { useIframeSrcMode } from "./iframe-src-mode"
import { connect, Connection, Methods, RemoteProxy, WindowMessenger } from "penpal/dist/penpal"
import { toast } from "react-toastify"

export type IFrameWidgetProps<ParentApi extends Methods, IFrameApi extends Methods> = {
  /** The widget that this IFrame is for (must be a 1-to-1 relation) */
  widget: string
  /** <iframe> element title for accessibility */
  title: string
  url: {
    /** URL to the index page of the folder with built assets. */
    prod: string
    /** Local dev URL, only for dev mode. Unavailable in production. */
    dev: string
  }
  /** The API provided to the child IFrame */
  parentApi: ParentApi
  /** Callback when IFrame connection is established */
  onConnectionEstablished: (api: RemoteProxy<IFrameApi>, connection: Connection<IFrameApi>) => void
  /** Miscellaneous props that you may want to pass to the iframe (e.g. styling) */
  iframeProps?: Omit<React.IframeHTMLAttributes<HTMLIFrameElement>, "src">
}

export type IFrameWidgetHandle = {
  iframe: () => HTMLIFrameElement | null
  /** Refresh the IFrame */
  refresh: () => void
}

export const IFrameWidget = forwardRefWithGenerics(function IFrameWidget<
  ParentApi extends Methods,
  IFrameApi extends Methods
>(props: IFrameWidgetProps<ParentApi, IFrameApi>, ref: React.Ref<IFrameWidgetHandle>) {
  const isDevMode = devMode()
  const [ srcMode, setSrcMode ] = useIframeSrcMode(props.widget)

  if (isDevMode && srcMode === undefined) {
    return (
      <div className="flex flex-col gap-2 justify-center items-center p-2">
        <div>
          <strong>{props.widget} source selector</strong>
        </div>
        <div className="text-sm">This is only visible in local development</div>
        <Button variant="outline" onClick={() => setSrcMode("dev")}>
          Use dev version
        </Button>
        <Button variant="outline" onClick={() => setSrcMode("prod")}>
          Use prod version
        </Button>
      </div>
    )
  }

  const src =
    isDevMode && srcMode === "dev"
      ? props.url.dev
      : props.url.prod

  return (
    <IFrameInner ref={ref} src={src} {...props} />
  )
})

const IFrameInner = forwardRefWithGenerics(function IFrameInner<
  ParentApi extends Methods,
  IFrameApi extends Methods
>(
  props: IFrameWidgetProps<ParentApi, IFrameApi> & { src: string },
  ref: React.Ref<IFrameWidgetHandle>
) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const connectionRef = useRef<Connection<IFrameApi> | null>()

  const connectIFrame = useMemoizedFn(async () => {
    const iframe = iframeRef.current
    if (!iframe) throw new Error(`Cannot establish ${props.widget} iframe communication: no iframe mounted`)
    if (!iframe.contentWindow) throw new Error(`Cannot establish ${props.widget} iframe communication: no contentWindow`)

    const messenger = new WindowMessenger({
      remoteWindow: iframe.contentWindow,
      allowedOrigins: [
        window.location.origin,
        /http:\/\/localhost:(.*)/
      ]
    })

    const connection = connect<IFrameApi>({
      messenger,
      methods: props.parentApi
    })
    connectionRef.current = connection

    try {
      const api = await connection.promise
      props.onConnectionEstablished(api, connection)
    } catch (e) {
      console.error("Error connecting iframe", e)
      throw e
    }
  })

  useImperativeHandle(ref, () => ({
    iframe: () => iframeRef.current,
    refresh: () => {
      connectionRef.current?.destroy()
      connectionRef.current = null
      const iframe = iframeRef.current
      if (iframe) iframe.src = `${iframe.src}`
      connectIFrame()
    }
  }), [ connectIFrame ])

  useEffect(() => {
    connectIFrame()
    return () => connectionRef.current?.destroy()
  }, [ props.src, connectIFrame ])

  return (
    <iframe
      ref={iframeRef}
      title={props.title}
      src={props.src}
      width="100%"
      height="100%"
      onLoad={e => {
        if (!devMode()) return
        // A bit of a flaky way to check if src is loaded, but it seems onError does not trigger on 404 and no way to get HTTP status...
        if (!e.currentTarget.contentDocument?.body.innerText.includes("Cannot GET")) return
        toast.error(`Failed to load ${props.widget} IFrame in dev environment. You probably didn't build it, to build: run yarn build in the relevant static-widgets subproject.`)
      }}
      {...props.iframeProps} />
  )
})

// https://www.totaltypescript.com/forwardref-with-generic-components#the-solution
function forwardRefWithGenerics<T, P = {}>(
  render: (props: P, ref: React.Ref<T>) => React.ReactNode
): (props: P & React.RefAttributes<T>) => React.ReactNode {
  // @ts-expect-error
  return React.forwardRef(render)
}
